import dateFormat from "dateformat";
import {
    isAfter as isAfterDateFns,
    isBefore as isBeforeDateFns,
    addMinutes as addMinutesDateFns,
} from "date-fns";
import { getOrdinalDay } from "../../calendar-common/helpers/formatDate";

export const translations = {
    ar: () =>
        import(
            /* webpackChunkName: 'dateformat-t-ar' */ "../translations/dateformat/ar"
        ),
    de: () =>
        import(
            /* webpackChunkName: 'dateformat-t-de' */ "../translations/dateformat/de"
        ),
    en: () =>
        import(
            /* webpackChunkName: 'dateformat-t-en' */ "../translations/dateformat/en"
        ),
    es: () =>
        import(
            /* webpackChunkName: 'dateformat-t-es' */ "../translations/dateformat/es"
        ),
    fr: () =>
        import(
            /* webpackChunkName: 'dateformat-t-fr' */ "../translations/dateformat/fr"
        ),
    id: () =>
        import(
            /* webpackChunkName: 'dateformat-t-id' */ "../translations/dateformat/id"
        ),
    ru: () =>
        import(
            /* webpackChunkName: 'dateformat-t-ru' */ "../translations/dateformat/ru"
        ),
    tr: () =>
        import(
            /* webpackChunkName: 'dateformat-t-tr' */ "../translations/dateformat/tr"
        ),
    "zh-cht": () =>
        import(
            /* webpackChunkName: 'dateformat-t-zh-cht' */ "../translations/dateformat/zh-cht"
        ),
    "zh-cn": () =>
        import(
            /* webpackChunkName: 'dateformat-t-zh-cn' */ "../translations/dateformat/zh-cn"
        ),
    vi: () =>
        import(
            /*webpackChunkName: 'dateformat-t-vi'*/ "../translations/dateformat/vi"
        ),
    fa: () =>
        import(
            /*webpackChunkName: 'dateformat-t-fa'*/ "../translations/dateformat/fa"
        ),
    pt: () =>
        import(
            /*webpackChunkName: 'dateformat-t-pt'*/ "../translations/dateformat/pt"
        ),
    cs: () =>
        import(
                /*webpackChunkName: 'dateformat-t-cs'*/ "../translations/dateformat/cs"
        ),
};

const UTCDATETIME_ISOSTRING_FORMAT = 'yyyy-mm-dd"T"HH:MM:ss"Z"';

export const convertDateToTimezone = (dateToConvert, userTimezoneOffset) => {
    const date = new Date(dateToConvert);
    const browserTimezone = date.getTimezoneOffset();
    const result = addMinutesDateFns(
        date,
        browserTimezone - userTimezoneOffset
    );
    return result;
};

export const convertDateToUserFriendly = (date) => {
    var re = /[T]|[Z]/gi;
    const result = date.replace(re, " ").trim();
    return result;
};

export const convertDateToUtc = (date, timezoneOffset) => {
    const result = addMinutesDateFns(new Date(date), timezoneOffset);
    return result;
};

export const nowDate = (timezoneOffset) => {
    const result = convertDateToTimezone(new Date(), timezoneOffset);
    return result;
};

export const isFuture = (date, timezoneOffset) => {
    const result = isAfterDateFns(date, nowDate(timezoneOffset));
    return result;
};

export const isPast = (date, timezoneOffset) => {
    const result = isBeforeDateFns(date, nowDate(timezoneOffset));
    return result;
};

export const getTimeInAdaptiveFormat = async (
    currentDate,
    dateNow,
    culture
) => {
    let result = "";

    const minuteDifference = Math.abs(
        Math.floor((currentDate - dateNow) / 1000 / 60)
    );

    if (minuteDifference < 60)
        result = await getMinutesAgo(culture, minuteDifference);
    if (minuteDifference >= 60)
        if (datesAreOnSameDay(new Date(currentDate), new Date(dateNow)))
            result = await getHoursGtmString(new Date(currentDate), culture);
        else result = await getDateGtmString(new Date(currentDate), culture);

    return result;
};

const datesAreOnSameDay = (first, second) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getUTCDate() === second.getUTCDate();

const getMinutesAgo = async (culture, minuteDifference) => {
    let result = "";
    let locale = culture.split("-")[0];
    await translations[locale.toLowerCase()]().then((x) => {
        let message = x.default.MinutesAgo;
        result = message.replace("{0}", minuteDifference + 1);
    });
    return result;
};

const getHoursGtmString = async (date, culture) => {
    if (date === undefined || date === null) {
        return "";
    }

    let locale = culture.split("-")[0];
    var splittedDate = date.toUTCString().split(" ");
    var splittedHours = splittedDate[4].split(":");
    var eventDate = "";
    await translations[locale.toLowerCase()]().then((x) => {
        eventDate = `${splittedHours[0]}:${splittedHours[1]} ${x.default.GMT}`;
    });

    return eventDate;
};

const getDateGtmString = async (date, culture) => {
    if (date === undefined || date === null) {
        return "";
    }

    var locale = culture.split("-")[0];
    var month = new Intl.DateTimeFormat(locale, { month: "short" }).format(
        date
    );

    let eventDate;
    await translations[locale.toLowerCase()]().then((x) => {
        var splittedDate = date.toUTCString().split(" ");
        var splittedHours = splittedDate[4].split(":");
        eventDate =
            month +
            " " +
            splittedDate[1] +
            ", " +
            splittedHours[0] +
            ":" +
            splittedHours[1] +
            ` ${x.default.GMT}`;
    });

    return eventDate;
};

const getDateLongGtmString = async (date, culture) => {
    if (date === undefined || date === null) {
        return "";
    }

    var locale = culture.split("-")[0];
    var month = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);

    let eventDate;
    await translations[locale.toLowerCase()]().then((x) => {
        var splittedDate = date.toUTCString().split(" ");
        var splittedHours = splittedDate[4].split(":");
        eventDate =
            month +
            " " +
            splittedDate[1] +
            ", " +
            splittedHours[0] +
            ":" +
            splittedHours[1] +
            ` ${x.default.GMT}`;
    });

    return eventDate;
};

export const dateFormatLocalized = async (date, formatStr, locale) => {
    if (!date) return "";

    if (!dateFormat.localeLoaded) {
        const t = await translations[locale.toLowerCase()]();
        dateFormat.localeLoaded = true;
        dateFormat.i18n = t.default;
        dateFormat.masks = { ...dateFormat.masks, ...t.default.masks };
    }

    return dateFormat(date, formatStr);
};

export const preloadDateFormatTranslation = (locale) => {
    if (!dateFormat.localeLoaded) {
        translations[locale.toLowerCase()]().then((x) => {
            dateFormat.i18n = x.default;
            dateFormat.localeLoaded = true;
            dateFormat.masks = { ...dateFormat.masks, ...x.default.masks };
        });
    }
};

export const dateFormatLocalizedSync = (date, formatStr) => {
    if (!date) return "";

    return dateFormat(date, formatStr);
};

export const getUtcDateTimeISOString = (date) =>
    dateFormat(date, UTCDATETIME_ISOSTRING_FORMAT);

export const getDateValuesGtm = async (date, culture) => {
    if (date === undefined || date === null) {
        return "";
    }
    let dateValues = null;
    var locale = culture.split("-")[0];
    await translations[locale.toLowerCase()]().then((x) => {
        dateValues = {
            month: new Intl.DateTimeFormat(locale, { month: "long" }).format(
                date
            ),
            weekday: new Intl.DateTimeFormat(locale, {
                weekday: "long",
            }).format(date),
            year: new Intl.DateTimeFormat(locale, { year: "numeric" }).format(
                date
            ),
            day: new Intl.DateTimeFormat(locale, { day: "numeric" }).format(
                date
            ),
            hour: `${new Intl.DateTimeFormat(locale, {
                hour: "numeric",
                minute: "numeric",
                timeZone: "UTC",
                hour12: false,
            }).format(new Date(date))} ${x.default.GMT}`
        };
    });

    if (locale !== "en") {
        return dateValues;
    }

    const pr = new Intl.PluralRules(locale, { type: "ordinal" });
    const day = `${dateValues.day} ${getOrdinalDay(pr.select(date.getDate()))}`;
    dateValues.day = day;
    return dateValues;
};
